import { createLogger } from "utils/logging";
import type { HTMLOcCinemaV1Element } from "@otto-ec/otto-components/cinema";
import { addLoadedTracking, addMiniActionsToVisibilityChanges, getElementPositionInPageContext, initBenefitExpiryCleanup, updateSheetUrlParams } from "../utils";
import type { Debug } from "@otto-ec/global-resources/debug";

const log = createLogger("list", "cinema");

/*                                                                                                    */
/*                                                                              */
const MAX_CLEANUP_PERIOD_IN_MILLIS = 20 * 24 * 60 * 60 * 1000;
const ROOT_SELECTOR = `[data-module*=ft9-benefit-cinema-productlist-root]`;

export class ListCinema {
	private trackedAsVisible: number[] = [];

	init() {
		log.info("FT-RepTile has duplicated the cinema -> going to call assets to init them");

		const containers = document.querySelectorAll<HTMLDivElement>(ROOT_SELECTOR);

		for (const container of containers) {
			const cinema = container.querySelector<HTMLOcCinemaV1Element>("oc-cinema-v1");
			if (!cinema) {
				log.info("no cinema found");
				break;
			} else if ("mounted" in cinema.dataset) {
				log.info("cinema already initialized");
				break;
			}
			this.initializeCurrentCinema(cinema);
		}

		/*                                     */
		document.addEventListener("oc-cinema-mount", (event: any) => {
			const mountedCinema = event.detail.element as HTMLOcCinemaV1Element;
			if (!ROOT_SELECTOR.includes(mountedCinema?.parentElement?.dataset.module ?? "undefined") || "mounted" in mountedCinema.dataset) return;
			this.initializeCurrentCinema(mountedCinema);
		});
	}

	private initializeCurrentCinema(cinema: HTMLOcCinemaV1Element): void {
		cinema.dataset.mounted = "true";

		const parentId = this.determineParentId(cinema);
		const featureOrder = getElementPositionInPageContext(cinema, "feature-index");

		cinema.addEventListener("tr-submit-tracking", ({ detail }) => {
			switch (true) {
				case detail.eventName === "tr-become-visible" && detail.method === "submitMiniAction":
					/*                                   */
					if (this.trackedAsVisible.includes(featureOrder)) {
						return;
					}
					addMiniActionsToVisibilityChanges(detail, cinema, "scroll", parentId);
					this.trackedAsVisible.push(featureOrder);
					break;
				case detail.eventName === "oc-item-visibility-changed" && detail.method === "submitMiniAction":
					if (detail.event.detail.scrollDirection === "left") {
						detail.abort();
						return;
					}
					log.info("cinema slide changed");
					addMiniActionsToVisibilityChanges(detail, cinema, "next", parentId);
					break;
				case detail.eventName === "tr-page-impression" && detail.method === "submitMerge": {
					/*                                                          */
					const firstCinema = featureOrder === 1 || featureOrder === 99;
					if (!firstCinema) {
						detail.abort();
						return;
					}
					const additionalLabels = this.getAdditionalLabels(cinema, parentId);
					addLoadedTracking(detail, cinema, featureOrder, log as unknown as Debug<string>, parentId, additionalLabels);
					break;
				}
				default:
					break;
			}
		});

		initBenefitExpiryCleanup(cinema, MAX_CLEANUP_PERIOD_IN_MILLIS, ROOT_SELECTOR);

		log.info("Receiving initialized event from asset for a cinema in position: %s -> going to register events and scarcity countdown", featureOrder);

		const params = { featurePosition: featureOrder.toString() };

		updateSheetUrlParams(cinema, params);
	}

	/**
 */
	private determineParentId(cinemaDom: HTMLOcCinemaV1Element): string {
		const topSlot = getElementPositionInPageContext(cinemaDom, "top-slot");
		const tileList = getElementPositionInPageContext(cinemaDom, "tilelist-slot");

		if (topSlot !== 99) {
			return `FT9-benefit-main-top-${topSlot}`;
		} else if (tileList !== 99) {
			return `FT9-benefit-main-tilelist-${tileList}`;
		} else {
			return "FT9-benefit-main-undefined-0";
		}
	}

	/**
 */
	private getAdditionalLabels(cinemaDom: HTMLOcCinemaV1Element, parentId: string): Record<string, string[]> {
		let additionalLabels: Record<string, string[]>;
		const cinemaCountLabelValue = getElementPositionInPageContext(cinemaDom, "feature-index");
		additionalLabels = {
			benefit_CinemaCount: [`${cinemaCountLabelValue === 99 ? 1 : cinemaCountLabelValue}`],
		};

		const slot = parentId.split("-")[3];

		if (slot && slot !== "undefined") additionalLabels = { ...additionalLabels, benefit_Slot: [slot] };

		return additionalLabels;
	}
}
